import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PawPrint, Plus, Trash2, UserRound } from 'lucide-react';
import { formatDate, type Lease, type Occupant, type OccupantKind } from '@vuokramani/shared';
import { Alert, Button, Dialog, DialogActions, Panel } from '../../components/ui.tsx';
import { errorKey } from '../../lib/api.ts';
import {
  useAddOccupant,
  useDeleteOccupant,
  useOccupants,
  useSetPetsAllowed,
} from '../../lib/tenancy.ts';
import s from './occupants.module.css';

/**
 * Asunnossa asuvat muut kuin sopimuksen vuokralaiset (puoliso, lapset) ja lemmikit.
 * Vuokralainen ja vuokranantaja voivat lisätä ja poistaa myös allekirjoituksen jälkeen.
 * Lemmikkejä voi lisätä vain, kun vuokranantaja on merkinnyt ne sallituiksi. Valinta on
 * erillään lukituista ehdoista, joten vuokranantaja voi muuttaa sitä milloin tahansa.
 */
export function Occupants({
  lease,
  editable,
  canSetPets = false,
}: {
  lease: Lease;
  editable: boolean;
  /** Vuokranantaja: lemmikit sallittu -valinta. */
  canSetPets?: boolean;
}) {
  const { t } = useTranslation();
  const { data: occupants } = useOccupants(lease.id);
  const people = (occupants ?? []).filter((o) => o.kind === 'person');
  const pets = (occupants ?? []).filter((o) => o.kind === 'pet');
  const petsAllowed = lease.pets === 'allowed' || lease.pets === 'by_agreement';

  return (
    <Panel>
      <h3 className={s.groupTitle}>{t('occupants.people')}</h3>
      <OccupantList
        items={people}
        leaseId={lease.id}
        editable={editable}
        empty={t('occupants.noPeople')}
      />
      {editable && <AddForm leaseId={lease.id} kind="person" />}

      <h3 className={`${s.groupTitle} ${s.second}`}>{t('occupants.pets')}</h3>
      {canSetPets && <PetsToggle lease={lease} />}
      {!petsAllowed && <p className={s.note}>{t('occupants.petsNotAllowed')}</p>}
      {/* Jo kirjatut lemmikit näkyvät, vaikka lemmikit myöhemmin kiellettäisiin. */}
      {(petsAllowed || pets.length > 0) && (
        <OccupantList
          items={pets}
          leaseId={lease.id}
          editable={editable}
          empty={t('occupants.noPets')}
        />
      )}
      {petsAllowed && editable && <AddForm leaseId={lease.id} kind="pet" />}
    </Panel>
  );
}

function PetsToggle({ lease }: { lease: Lease }) {
  const { t } = useTranslation();
  const setPets = useSetPetsAllowed(lease.id);
  const allowed = lease.pets === 'allowed' || lease.pets === 'by_agreement';
  return (
    <div className={s.toggle}>
      <label className={s.toggleLabel}>
        <input
          type="checkbox"
          // Näytetään valinta heti, vaikka tallennus on vielä kesken.
          checked={setPets.isPending ? Boolean(setPets.variables) : allowed}
          disabled={setPets.isPending}
          onChange={(e) => setPets.mutate(e.target.checked)}
        />
        <span>{t('occupants.petsAllowed')}</span>
      </label>
      <p className={s.note}>{t('occupants.petsToggleHint')}</p>
      {setPets.isError && <Alert>{t(errorKey(setPets.error))}</Alert>}
    </div>
  );
}

function OccupantList({
  items,
  leaseId,
  editable,
  empty,
}: {
  items: Occupant[];
  leaseId: string;
  editable: boolean;
  empty: string;
}) {
  const { t, i18n } = useTranslation();
  const remove = useDeleteOccupant(leaseId);
  const [deleting, setDeleting] = useState<Occupant | null>(null);
  if (items.length === 0) return <p className={s.note}>{empty}</p>;
  return (
    <>
      <ul className={s.list}>
        {items.map((o) => (
          <li key={o.id} className={s.row}>
            <span className={s.icon} aria-hidden="true">
              {o.kind === 'pet' ? <PawPrint size={16} /> : <UserRound size={16} />}
            </span>
            <span className={s.text}>
              <span className={s.name}>
                {o.name}
                {o.species && <span className={s.species}> · {o.species}</span>}
              </span>
              <span className={s.meta}>
                {o.addedByName
                  ? t('occupants.addedBy', {
                      name: o.addedByName,
                      date: formatDate(o.createdAt, i18n.language),
                    })
                  : t('occupants.addedOn', { date: formatDate(o.createdAt, i18n.language) })}
              </span>
            </span>
            {editable && (
              <Button
                variant="ghost"
                icon
                aria-label={t('common.delete')}
                title={t('common.delete')}
                onClick={() => setDeleting(o)}
              >
                <Trash2 size={16} />
              </Button>
            )}
          </li>
        ))}
      </ul>
      <Dialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title={t('occupants.deleteTitle')}
      >
        <p>{deleting && t('occupants.deleteText', { name: deleting.name })}</p>
        <DialogActions>
          <Button variant="ghost" onClick={() => setDeleting(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={remove.isPending}
            onClick={() =>
              deleting && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
            }
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Lisäys rivin alla: henkilölle nimi, lemmikille laji ja nimi. */
function AddForm({ leaseId, kind }: { leaseId: string; kind: OccupantKind }) {
  const { t } = useTranslation();
  const add = useAddOccupant(leaseId);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [species, setSpecies] = useState('');

  if (!open) {
    return (
      <Button variant="ghost" className={s.addButton} onClick={() => setOpen(true)}>
        <Plus size={16} aria-hidden="true" />
        {kind === 'pet' ? t('occupants.addPet') : t('occupants.addPerson')}
      </Button>
    );
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    add.mutate(
      { kind, name: name.trim(), species: kind === 'pet' ? species.trim() : null },
      {
        onSuccess: () => {
          setName('');
          setSpecies('');
          setOpen(false);
        },
      },
    );
  }

  return (
    <form className={s.form} onSubmit={submit}>
      {kind === 'pet' && (
        <input
          className={s.input}
          placeholder={t('occupants.speciesPlaceholder')}
          aria-label={t('occupants.species')}
          value={species}
          maxLength={60}
          onChange={(e) => setSpecies(e.target.value)}
        />
      )}
      <input
        className={s.input}
        placeholder={
          kind === 'pet' ? t('occupants.petNamePlaceholder') : t('occupants.namePlaceholder')
        }
        aria-label={t('occupants.name')}
        value={name}
        maxLength={100}
        autoFocus={kind === 'person'}
        onChange={(e) => setName(e.target.value)}
      />
      <Button type="submit" variant="primary" disabled={!name.trim() || add.isPending}>
        {t('occupants.add')}
      </Button>
      <Button variant="ghost" onClick={() => setOpen(false)}>
        {t('common.cancel')}
      </Button>
      {add.isError && <Alert>{t(errorKey(add.error))}</Alert>}
    </form>
  );
}
